import { openDB } from 'idb';

// Will be changed to uppercase) to do
const dbName = 'post2db';
const additionalData = 'additional_data';
const assignmentsTable = 'assignments';
const profileTable = 'profile';
const readyTable = 'ready';
const retailersTable = 'retailers';
const formsTable = 'forms';
const surveysTable = 'surveys';
const offlineAssignmentsTable = 'offline_assignments';
const surveyDraftsTable = 'survey_drafts';
// Key inside `additional_data` holding the cached media tag vocabulary.
const MEDIA_TAGS_KEY = 'media_tags';
const version = 3;

const initiateIDB = async () => {
  const db = await openDB(dbName, version, {
    // eslint-disable-next-line no-unused-vars
    async upgrade(upgrdDB, oldVersion, newVersion, transaction) {
      if (oldVersion < 1) {
        upgrdDB.createObjectStore(additionalData);
        upgrdDB.createObjectStore(assignmentsTable);
        upgrdDB.createObjectStore(profileTable);
        upgrdDB.createObjectStore(readyTable);
        upgrdDB.createObjectStore(retailersTable);
        upgrdDB.createObjectStore(formsTable);
        upgrdDB.createObjectStore(surveysTable);
      }
      if (oldVersion < 2) {
        upgrdDB.createObjectStore(offlineAssignmentsTable);
      }
      if (oldVersion < 3) {
        upgrdDB.createObjectStore(surveyDraftsTable);
      }
    },
  });
  return db;
};

const deleteIDB = async () => {
  const db = await initiateIDB();
  await db.transaction(additionalData, 'readwrite').objectStore(additionalData).clear();
  await db.transaction(assignmentsTable, 'readwrite').objectStore(assignmentsTable).clear();
  await db.transaction(profileTable, 'readwrite').objectStore(profileTable).clear();
  await db.transaction(readyTable, 'readwrite').objectStore(readyTable).clear();
  await db.transaction(retailersTable, 'readwrite').objectStore(retailersTable).clear();
  await db.transaction(formsTable, 'readwrite').objectStore(formsTable).clear();
};

const putOfflineAssignment = async (id, data) => {
  const db = await initiateIDB();
  await db.transaction(offlineAssignmentsTable, 'readwrite').objectStore(offlineAssignmentsTable).put(data, id);
};

const putRetailer = async (id, data) => {
  const db = await initiateIDB();
  await db.transaction(retailersTable, 'readwrite').objectStore(retailersTable).put(data, id);
};

const putForm = async (id, data) => {
  const db = await initiateIDB();
  await db.transaction(formsTable, 'readwrite').objectStore(formsTable).put(data, id);
};

const putProfile = async (data) => {
  const db = await initiateIDB();
  await db.transaction(profileTable, 'readwrite').objectStore(profileTable).put(data, 'profile');
};

const setReady = async (isReady) => {
  const db = await initiateIDB();
  await db.transaction(readyTable, 'readwrite').objectStore(readyTable).put({ isReady, readyAt: new Date() }, 'ready');
};

const getOfflineAssignment = async (id) => {
  const db = await initiateIDB();
  return db.transaction(offlineAssignmentsTable).objectStore(offlineAssignmentsTable).get(id);
};

const getOfflineAssignments = async () => {
  const db = await initiateIDB();
  const tx = db.transaction(offlineAssignmentsTable);
  const store = tx.objectStore(offlineAssignmentsTable);
  const [keys, values] = await Promise.all([store.getAllKeys(), store.getAll()]);
  return keys.map((id, idx) => ({ id, data: values[idx] }));
};

const removeOfflineAssignment = async (id) => {
  const db = await initiateIDB();
  await db.transaction(offlineAssignmentsTable, 'readwrite').objectStore(offlineAssignmentsTable).delete(id);
};

const getUnsyncedSurveys = async () => {
  const db = await initiateIDB();
  const tx = db.transaction(surveysTable);
  const store = tx.objectStore(surveysTable);
  const [keys, values] = await Promise.all([store.getAllKeys(), store.getAll()]);
  return keys.map((key, idx) => ({ text: key, data: values[idx] }));
};

const countUnsyncedSurveys = async () => {
  const db = await initiateIDB();
  return db.transaction(surveysTable).objectStore(surveysTable).count();
};

const removeUnsyncedSurvey = async (key) => {
  const db = await initiateIDB();
  const surveyData = await db.transaction(surveysTable).objectStore(surveysTable).get(key);
  const offlineId = surveyData?.data?.attributes?.__offlineId__;
  if (offlineId) {
    await db.transaction(additionalData, 'readwrite').objectStore(additionalData).delete(`pending_files_${offlineId}`);
  }
  await db.transaction(surveysTable, 'readwrite').objectStore(surveysTable).delete(key);
};

const getPendingFiles = async (offlineId) => {
  if (!offlineId) return null;
  const db = await initiateIDB();
  return db.transaction(additionalData).objectStore(additionalData).get(`pending_files_${offlineId}`);
};

const removePendingFiles = async (offlineId) => {
  if (!offlineId) return;
  const db = await initiateIDB();
  await db.transaction(additionalData, 'readwrite').objectStore(additionalData).delete(`pending_files_${offlineId}`);
};

// --- Media tag vocabulary ----------------------------------------------------
// Kept in `additional_data` under one fixed key rather than a store of its own, so no IDB version
// bump (and no upgrade path for every installed client) is needed for a list this small. Written
// whenever GET /media-tags succeeds and when assignments are staged for offline; read back when the
// request fails, so the tag pickers on the survey form still have names and options offline.

const putMediaTags = async (tags) => {
  const db = await initiateIDB();
  await db.transaction(additionalData, 'readwrite').objectStore(additionalData).put(tags, MEDIA_TAGS_KEY);
};

const getMediaTags = async () => {
  const db = await initiateIDB();
  return db.transaction(additionalData).objectStore(additionalData).get(MEDIA_TAGS_KEY);
};

// --- In-progress survey drafts (autosaved while filling CreateSurvey) --------
// Keyed by `userId:formId:retailerId:assignmentId` so a draft is scoped to the
// exact form/retailer/assignment a given user is working on. Intentionally kept
// out of clearOfflineData so the "Clear offline data" control can't wipe an
// in-progress survey.

const putSurveyDraft = async (key, data) => {
  const db = await initiateIDB();
  await db.transaction(surveyDraftsTable, 'readwrite').objectStore(surveyDraftsTable).put(data, key);
};

const getSurveyDraft = async (key) => {
  const db = await initiateIDB();
  return db.transaction(surveyDraftsTable).objectStore(surveyDraftsTable).get(key);
};

// Every draft still in the store is by definition unsubmitted — a successful submit deletes
// it. Returns raw `{ key, data }` pairs; scoping to the current user and filtering out
// empty shells is the caller's job (see hooks/useUnfinishedDrafts).
const getSurveyDrafts = async () => {
  const db = await initiateIDB();
  const tx = db.transaction(surveyDraftsTable);
  const store = tx.objectStore(surveyDraftsTable);
  const [keys, values] = await Promise.all([store.getAllKeys(), store.getAll()]);
  return keys.map((key, index) => ({ key, data: values[index] }));
};

const removeSurveyDraft = async (key) => {
  const db = await initiateIDB();
  await db.transaction(surveyDraftsTable, 'readwrite').objectStore(surveyDraftsTable).delete(key);
};

// Drops every draft whose updatedAt is older than maxAgeMs. Best-effort cleanup
// run on entry so stale drafts don't linger forever.
const pruneExpiredSurveyDrafts = async (maxAgeMs) => {
  const db = await initiateIDB();
  const readTx = db.transaction(surveyDraftsTable);
  const store = readTx.objectStore(surveyDraftsTable);
  const [keys, values] = await Promise.all([store.getAllKeys(), store.getAll()]);
  const now = Date.now();
  const expiredKeys = keys.filter((_, idx) => {
    const updatedAt = values[idx]?.updatedAt;
    return !updatedAt || now - new Date(updatedAt).getTime() > maxAgeMs;
  });
  await Promise.all(
    expiredKeys.map((key) => db.transaction(surveyDraftsTable, 'readwrite').objectStore(surveyDraftsTable).delete(key)),
  );
};

// Wipes every store the offline workflow uses, including the staged
// assignments and the offline_mode flag (so the user is dropped back online).
// The IDB itself is preserved so version/upgrade state is intact.
const clearOfflineData = async () => {
  const db = await initiateIDB();
  const stores = [
    additionalData,
    assignmentsTable,
    profileTable,
    readyTable,
    retailersTable,
    formsTable,
    surveysTable,
    offlineAssignmentsTable,
  ];
  await Promise.all(stores.map((name) => db.transaction(name, 'readwrite').objectStore(name).clear()));
};

export default {
  initiateIDB,
  deleteIDB,
  putOfflineAssignment,
  getOfflineAssignment,
  getOfflineAssignments,
  removeOfflineAssignment,
  putRetailer,
  putForm,
  putProfile,
  setReady,
  clearOfflineData,
  getUnsyncedSurveys,
  countUnsyncedSurveys,
  removeUnsyncedSurvey,
  getPendingFiles,
  removePendingFiles,
  putMediaTags,
  getMediaTags,
  putSurveyDraft,
  getSurveyDraft,
  getSurveyDrafts,
  removeSurveyDraft,
  pruneExpiredSurveyDrafts,
};
